"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropType } from "@/generated/models/ApiDropType";
import { DropSize, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { WsDropUpdateMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { useDebouncedQueryRefetch } from "./useDebouncedQueryRefetch";
const DEFAULT_WAVE_DROPS_LIMIT = 20;
interface UseWaveDropsProps {
  readonly waveId: string;
  readonly dropType?: ApiDropType | undefined;
  readonly containsMedia?: boolean | undefined;
  readonly limit?: number | undefined;
  readonly enabled?: boolean | undefined;
}
const processDrops = (pages: ApiDrop[][] | undefined): ExtendedDrop[] => {
  if (!pages) {
    return [];
  }
  const allDrops = pages.flat();
  const extendedDrops: ExtendedDrop[] = allDrops.map(drop => ({
    ...drop,
    type: DropSize.FULL,
    stableKey: drop.id,
    stableHash: drop.id
  }));
  const keyCount = new Map<string, number>();
  return extendedDrops.map(drop => {
    const count = (keyCount.get(drop.stableKey) ?? 0) + 1;
    keyCount.set(drop.stableKey, count);
    if (count > 1) {
      return {
        ...drop,
        stableKey: `${drop.stableKey}-${count}`
      };
    }
    return drop;
  });
};
export function useWaveDrops(t0) {
  const $ = _c(34);
  const {
    waveId,
    dropType,
    containsMedia: t1,
    limit: t2,
    enabled: t3
  } = t0;
  const containsMedia = t1 === undefined ? false : t1;
  const limit = t2 === undefined ? DEFAULT_WAVE_DROPS_LIMIT : t2;
  const enabled = t3 === undefined ? true : t3;
  const t4 = dropType ?? null;
  let t5;
  if ($[0] !== containsMedia || $[1] !== limit || $[2] !== t4 || $[3] !== waveId) {
    t5 = [QueryKey.DROPS, {
      waveId,
      limit,
      dropType: t4,
      containsMedia,
      context: "wave-drops"
    }];
    $[0] = containsMedia;
    $[1] = limit;
    $[2] = t4;
    $[3] = waveId;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const queryKey = t5 as const;
  let t6;
  if ($[5] !== containsMedia || $[6] !== dropType || $[7] !== limit || $[8] !== waveId) {
    t6 = async t7 => {
      const {
        pageParam
      } = t7;
      const params = {
        wave_id: waveId,
        limit: limit.toString()
      };
      if (containsMedia) {
        params.contains_media = "true";
      }
      if (dropType !== undefined) {
        params.drop_type = dropType;
      }
      if (typeof pageParam === "number") {
        params.serial_no_less_than = `${pageParam}`;
      }
      return await commonApiFetch({
        endpoint: "drops",
        params
      });
    };
    $[5] = containsMedia;
    $[6] = dropType;
    $[7] = limit;
    $[8] = waveId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = enabled && !!waveId;
  let t8;
  if ($[10] !== queryKey || $[11] !== t6 || $[12] !== t7) {
    t8 = {
      queryKey,
      queryFn: t6,
      enabled: t7,
      initialPageParam: null,
      getNextPageParam: _temp,
      placeholderData: keepPreviousData,
      staleTime: 60000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true
    };
    $[10] = queryKey;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const {
    data,
    fetchNextPage: onFetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t8);
  let t9;
  if ($[14] !== hasNextPage || $[15] !== isFetchingNextPage || $[16] !== onFetchNextPage) {
    t9 = async () => {
      if (hasNextPage && !isFetchingNextPage) {
        await onFetchNextPage();
      }
    };
    $[14] = hasNextPage;
    $[15] = isFetchingNextPage;
    $[16] = onFetchNextPage;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const fetchNextPage = t9;
  data?.pages;
  const t10 = data?.pages;
  let t11;
  if ($[18] !== t10) {
    t11 = processDrops(t10);
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const drops = t11;
  let t12;
  if ($[20] !== isFetching || $[21] !== isFetchingNextPage || $[22] !== refetch) {
    t12 = {
      refetch,
      isFetching,
      isFetchingNextPage
    };
    $[20] = isFetching;
    $[21] = isFetchingNextPage;
    $[22] = refetch;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const requestRefetch = useDebouncedQueryRefetch(t12);
  let t13;
  if ($[24] !== requestRefetch || $[25] !== waveId) {
    t13 = message => {
      if (waveId !== message.wave.id) {
        return;
      }
      requestRefetch();
    };
    $[24] = requestRefetch;
    $[25] = waveId;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t13);
  let t14;
  if ($[27] !== drops || $[28] !== fetchNextPage || $[29] !== hasNextPage || $[30] !== isFetching || $[31] !== isFetchingNextPage || $[32] !== refetch) {
    t14 = {
      drops,
      fetchNextPage,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
      refetch
    };
    $[27] = drops;
    $[28] = fetchNextPage;
    $[29] = hasNextPage;
    $[30] = isFetching;
    $[31] = isFetchingNextPage;
    $[32] = refetch;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}
function _temp(lastPage) {
  return lastPage.at(-1)?.serial_no ?? undefined;
}